const CACHE_PREFIX = "fpep_cms_news_cache_v2";
const DEFAULT_CACHE_TTL_MS = 12 * 60 * 60 * 1000;

const parsePositiveIntegerEnv = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const cacheTtlMs = parsePositiveIntegerEnv(
  process.env.REACT_APP_CMS_FALLBACK_CACHE_TTL_MS,
  DEFAULT_CACHE_TTL_MS,
);

function slugify(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/gi, "dj")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

export function getLocalNewsSlug(post) {
  return post?.attributes?.Slug || slugify(post?.attributes?.Title || "novost");
}

export function extractLocalNewsId(routeParam = "") {
  const match = String(routeParam || "").match(/^(\d+)/);
  return match ? match[1] : routeParam;
}

export async function fetchLocalNews() {
  const response = await fetch("/data/blogData.json", {
    headers: { Accept: "application/json" },
    cache: "no-cache",
  });

  if (!response.ok) {
    throw new Error("Local news JSON could not be loaded.");
  }

  const data = await response.json();
  return [...data].sort((a, b) => b.id - a.id);
}

export function getLocalNewsPage(posts = [], pageIndex = 0, postsPerPage = 4, latestLimit = 5) {
  const offset = pageIndex * postsPerPage;
  const pageCount = Math.ceil(posts.length / postsPerPage);

  return {
    posts: posts.slice(offset, offset + postsPerPage),
    latestPosts: posts.slice(0, latestLimit),
    pageCount,
  };
}

export function findLocalNewsPost(posts = [], routeParam = "", extractSlugFromRouteParam = (value) => value) {
  const numericId = extractLocalNewsId(routeParam);
  const routeSlug = extractSlugFromRouteParam(routeParam);

  return posts.find((item) => {
    const itemId = item.id?.toString();
    const itemSlug = getLocalNewsSlug(item);

    return itemId === numericId || itemSlug === routeSlug || `${itemId}-${itemSlug}` === routeParam;
  });
}

function canUseStorage() {
  try {
    return typeof window !== "undefined" && Boolean(window.localStorage);
  } catch (_error) {
    return false;
  }
}

function getCacheKey(key) {
  return `${CACHE_PREFIX}:${key}`;
}

function normalizeCacheLocale(locale = "me") {
  return String(locale || "me").toLowerCase().startsWith("en") ? "en" : "me";
}

function localizeCacheKey(key, locale = "me") {
  return `locale:${normalizeCacheLocale(locale)}:${key}`;
}

function readCache(key) {
  if (!canUseStorage()) return null;

  try {
    const raw = window.localStorage.getItem(getCacheKey(key));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const createdAt = Number(parsed?.createdAt || 0);
    const ttl = Number(parsed?.ttlMs || cacheTtlMs);

    if (!parsed || !createdAt || Date.now() - createdAt > ttl) {
      window.localStorage.removeItem(getCacheKey(key));
      return null;
    }

    return parsed.payload || null;
  } catch (_error) {
    return null;
  }
}

function writeCache(key, payload) {
  if (!canUseStorage() || payload === undefined || payload === null) return;

  try {
    window.localStorage.setItem(
      getCacheKey(key),
      JSON.stringify({
        createdAt: Date.now(),
        ttlMs: cacheTtlMs,
        payload,
      }),
    );
  } catch (_error) {
    // LocalStorage can fail in private mode or when quota is full. The site must keep working.
  }
}

export function cacheCmsNewsPage(pageIndex, postsPerPage, payload, locale = "me") {
  writeCache(localizeCacheKey(`news-page:${pageIndex}:limit:${postsPerPage}`, locale), payload);
}

export function getCachedCmsNewsPage(pageIndex, postsPerPage, locale = "me") {
  return readCache(localizeCacheKey(`news-page:${pageIndex}:limit:${postsPerPage}`, locale));
}

export function cacheCmsLatestPosts(latestLimit, posts = [], locale = "me") {
  writeCache(localizeCacheKey(`latest:${latestLimit}`, locale), posts);
}

export function getCachedCmsLatestPosts(latestLimit, locale = "me") {
  return readCache(localizeCacheKey(`latest:${latestLimit}`, locale)) || [];
}

export function cacheCmsFeaturedPosts(featuredLimit, posts = [], locale = "me") {
  writeCache(localizeCacheKey(`featured:${featuredLimit}`, locale), posts);
}

export function getCachedCmsFeaturedPosts(featuredLimit, locale = "me") {
  return readCache(localizeCacheKey(`featured:${featuredLimit}`, locale)) || [];
}

export function cacheCmsNewsDetail(slug, post, locale = "me") {
  if (!slug || !post) return;

  writeCache(localizeCacheKey(`detail:${slug}`, locale), post);

  if (post?.attributes?.Slug && post.attributes.Slug !== slug) {
    writeCache(localizeCacheKey(`detail:${post.attributes.Slug}`, locale), post);
  }
}

export function getCachedCmsNewsDetail(slug, locale = "me") {
  if (!slug) return null;
  return readCache(localizeCacheKey(`detail:${slug}`, locale));
}

export function isNotFoundError(error) {
  return error?.status === 404 || error?.code === "PUBLIC_NEWS_NOT_FOUND";
}

export function isIntentionalCmsDisabled(error) {
  return error?.code === "CMS_DISABLED";
}

export function shouldSuppressFallbackNotice(error) {
  return isNotFoundError(error) || isIntentionalCmsDisabled(error);
}

export function createFallbackNotice({ source = "local", reason = "unavailable", context = "list" } = {}) {
  if (source === "cache") {
    return context === "detail"
      ? "CMS trenutno nije dostupan. Prikazana je posljednja sačuvana verzija ove objave."
      : "CMS trenutno nije dostupan. Prikazane su posljednje sačuvane novosti.";
  }

  if (reason === "empty") {
    return "CMS trenutno nema objavljenih novosti. Prikazane su postojeće lokalne objave.";
  }

  if (context === "detail") {
    return "CMS trenutno nije dostupan za ovu objavu. Prikazana je postojeća lokalna verzija ako postoji.";
  }

  return "CMS trenutno nije dostupan. Prikazane su postojeće lokalne objave.";
}

export function getFallbackBadge(source = "local") {
  if (source === "cache") return "Cache";
  if (source === "local") return "Lokalna rezerva";
  return "CMS";
}
